import { useWeb3React } from '@web3-react/core'
import createDependableResource from 'utils/zustand/createDependableResource'
import createPeriodicRefresher from 'utils/createPeriodicRefresher'
import { getAccount, onAccountInitOrChange } from 'state/web3/account'
import BigNumber from 'bignumber.js'
import { DEFAULT_TOKEN_DECIMAL } from 'config'
import { farmsConfig } from 'config/constants'
import { getAddress, getConditionalLiquidityAddress } from 'utils/addressHelpers'
import multicall from 'utils/multicall'
import conditionalLiquidityABI from 'config/abi/conditionalLiquidity.json'

const useClpEarningsResource = createDependableResource<number>({
  initValue: 0,
  getValue: async () => {
    const account = getAccount()
    if (!account) return 0

    const calls = farmsConfig.map((farm) => ({
      address: getConditionalLiquidityAddress(),
      name: 'pendingVVS',
      params: [getAddress(farm.lpAddresses), account],
    }))

    const rawResults = await multicall(conditionalLiquidityABI, calls)
    const results = farmsConfig.map((farm, index) => ({ ...farm, balance: new BigNumber(rawResults[index]) }))
    const farmsWithBalances = results.filter((balanceType) => balanceType.balance.gt(0))
    return farmsWithBalances.reduce((accum, earning) => {
      const earningNumber = new BigNumber(earning.balance)
      if (earningNumber.eq(0)) {
        return accum
      }
      return accum + earningNumber.div(DEFAULT_TOKEN_DECIMAL).toNumber()
    }, 0)
  },
  onInit: ({ resetValue, refresh, getIsStarted }) => {
    onAccountInitOrChange(() => {
      if (!getIsStarted()) return
      resetValue()
      refresh()
    })
  },
  dbgName: 'ClpEarnings',
})

const usePeriodicRefreshClpEarnings = createPeriodicRefresher(
  async () => {
    await useClpEarningsResource.getState().refresh()
  },
  {
    frequency: 'normal',
    dbgName: 'PollClpEarnings',
  },
)

const useClpEarnings = () => {
  const { account } = useWeb3React()
  usePeriodicRefreshClpEarnings(Boolean(account))
  return useClpEarningsResource((s) => s.value)
}

export default useClpEarnings
